import { useEffect, useState } from 'react';
import { Tag } from 'lucide-react';
import { Button, CenteredSpinner } from '@/components/ui';
import { api } from '@/lib/api';
import { t } from '@/lib/i18n';

/**
 * The editable tagging prompt — parity with Pimcore keeping the prompt in a DB
 * row so tagging behaviour is tunable without a deploy.
 */
export function PromptPage() {
  const [prompt, setPrompt] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.getPrompt().then((r) => setPrompt(r.prompt));
  }, []);

  if (prompt === null) return <CenteredSpinner label={t('promptPage.loadingPrompt')} />;

  return (
    <div className="scroll-thin h-full overflow-y-auto">
      <div className="w-full px-6 py-6">
        <div className="mb-4 flex items-center gap-2">
          <Tag className="size-5 text-brand-500" />
          <h1 className="text-lg font-semibold text-strong">{t('promptPage.taggingPrompt')}</h1>
        </div>
        <p className="mb-3 text-sm text-muted">
          {t('promptPage.promptDescription')}
        </p>
        <textarea
          value={prompt}
          onChange={(e) => { setPrompt(e.target.value); setSaved(false); }}
          rows={16}
          // The page is full-width, but a monospace editor stretched across a
          // wide monitor is unreadable — cap the measure, not the page.
          className="w-full max-w-[110ch] resize-y rounded-lg border line surface-card px-3 py-2 font-mono text-[13px] text-strong focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <div className="mt-3 flex items-center gap-3">
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              await api.setPrompt(prompt).catch(() => {});
              setBusy(false);
              setSaved(true);
            }}
          >
            {t('promptPage.save')}
          </Button>
          {saved && <span className="text-xs text-brand-600">{t('promptPage.savedCheckmark')}</span>}
        </div>
      </div>
    </div>
  );
}
